import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectAuth } from '../../redux/auth/selectors';
import { selectTreeIndex } from '../../redux/context/selectors';
import { useCurrentNodeId } from '../../redux/context/navigation';
import { ancestorsOf, consolePathFor } from '../../redux/context/treeUtils';
import { buildMenu, MenuLevel } from './menus';
import { nodeSettings } from '../../service/delegation.service';
import { getNodeUserCount } from '../../service/user.service';
import {
  listEntityInheritedMembers
} from '../../service/workspaceMembers.service';
import { CONSOLE_USERS_CHANGED } from '../features/console/NodeUsers';
import { useCapability } from '../hooks/useSelfCapabilities';
import { accessLabel } from './accessLabel';
import ResourceShell from './resource/ResourceShell';

import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

/* -------------------------------------------------------------------------- */
/*                                CONSTANTS                                   */
/* -------------------------------------------------------------------------- */
const DefaultLayout: React.FC = () => {
  /* ------------------------- ROUTER & REDUX HOOKS ------------------------- */
  const location = useLocation();
  const navigate = useNavigate();
  const { current } = useSelector(selectAuth);

  /* ------------------------------ CAPABILITIES ---------------------------- */
  const has_cve_view = useCapability('REFERENCE_DATA');
  const has_access_view = useCapability('MEMBERSHIP');
  const is_admin = useCapability('WORKSPACE', 'manage');
  /*
   * Settings is no longer one capability. The audit trail lives there and AUDIT_LOG.read
   * reaches a CISO, who sits at WORKSPACE.read — so the entry has to open for whoever
   * holds any tab behind it, and each tab gates itself.
   */
  const has_audit_view = useCapability('AUDIT_LOG');
  const has_role_config = useCapability('ROLE_CONFIG', 'manage');
  const has_settings_view = is_admin || has_audit_view || has_role_config;

  /* ------------------------------ MENU DATA ------------------------------ */
  const treeIndex = useSelector(selectTreeIndex);
  const nodeId = useCurrentNodeId();
  const defaultWs = current.default_workspace;
  const level: MenuLevel = location.pathname.startsWith('/org/')
    ? 'organisation'
    : location.pathname.startsWith('/entity/')
      ? 'entity'
      : 'workspace';

  const consoleTreeNode = level !== 'workspace' && nodeId != null ? treeIndex?.[nodeId] : undefined;
  const consoleNode = consoleTreeNode && level !== 'workspace'
    ? { name: consoleTreeNode.name, kind: level }
    : undefined;

  let upTo: { name: string; path: string } | undefined;
  if (level === 'workspace' && defaultWs?.id != null && treeIndex) {
    const parent = ancestorsOf(treeIndex, defaultWs.id)
      .slice(0, -1)
      .reverse()
      .find((n) => n.kind === 'entity');
    const path = parent ? consolePathFor(parent) : null;
    if (parent && path) upTo = { name: parent.name, path };
  }

  const canDelegate = useCapability('DELEGATION', 'manage', nodeId ?? undefined);

  // An entity console lists a setting only while it is delegated to that entity.
  const [delegatedHere, setDelegatedHere] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (level !== 'entity' || nodeId == null) {
      setDelegatedHere({});
      return;
    }
    let cancelled = false;
    nodeSettings(nodeId)
      .then((settings) => {
        if (cancelled) return;
        setDelegatedHere(
          Object.fromEntries(Object.entries(settings).map(([k, v]) => [k, Boolean(v.delegated)]))
        );
      })
      .catch(() => {
        if (!cancelled) setDelegatedHere({});
      });
    return () => {
      cancelled = true;
    };
  }, [level, nodeId]);
  // The count beside the console's Users tab: hidden while loading or on error.
  const [usersCount, setUsersCount] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (level === 'workspace' || nodeId == null) {
      setUsersCount(undefined);
      return;
    }
    let cancelled = false;
    const load = () => {
      // An entity's table also lists the people who reach it through a role above.
      const inheritedCount = level === 'entity'
        ? listEntityInheritedMembers(nodeId).then((list) => list.length).catch(() => 0)
        : Promise.resolve(0);
      Promise.all([getNodeUserCount(nodeId, level), inheritedCount])
        .then(([total, extra]) => total + extra)
        .then((n) => {
          if (!cancelled) setUsersCount(Number.isFinite(n) ? n : undefined);
        })
        .catch(() => {
          if (!cancelled) setUsersCount(undefined);
        });
    };
    setUsersCount(undefined);
    load();
    window.addEventListener(CONSOLE_USERS_CHANGED, load);
    return () => {
      cancelled = true;
      window.removeEventListener(CONSOLE_USERS_CHANGED, load);
    };
  }, [level, nodeId]);
  const menuData = buildMenu(level, {
    nodeId: nodeId ?? undefined,
    hasCveView: has_cve_view,
    hasSettingsView: has_settings_view,
    hasAccessView: has_access_view,
    upTo,
    consoleExtras: {
      delegation: canDelegate,
      accessLabel: accessLabel(nodeId != null ? treeIndex?.[nodeId] : undefined, treeIndex),
      settings: delegatedHere,
      usersCount
    }
  });

  /* A saved default that is a pre-1a org or entity lands on its console. */
  const isConsoleRoute =
    location.pathname.startsWith('/org/') || location.pathname.startsWith('/entity/');
  useEffect(() => {
    const kind = defaultWs?.kind ?? (defaultWs?.id != null ? treeIndex?.[defaultWs.id]?.kind : undefined);
    if (kind && kind !== 'workspace' && !isConsoleRoute) {
      const target = consolePathFor({ ...defaultWs, kind });
      if (target) navigate(target, { replace: true });
    }
  }, [defaultWs, treeIndex, isConsoleRoute, navigate]);

  /* ------------------------------- RENDER --------------------------------- */
  const resource = consoleNode
    ? { name: consoleNode.name, kind: consoleNode.kind }
    : { 
      name: defaultWs?.name ?? 'Select a resource', 
      kind: 'workspace' as const 
    };

  return (
    <ResourceShell
      menu={menuData}
      sidebarLabel={level === 'workspace' ? 'WORKSPACE' : 'ADMINISTRATION'}
      resource={resource}
    />
  );
};

export default DefaultLayout;
